import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Spin, Input, Button } from 'antd';
import axios from 'axios';
import AuthShell from './AuthShell';
import { logout } from '../authActions';
import { connect } from 'react-redux';

// Enjo-style "Choose your workspace" screen shown after sign-in/sign-up.
// Lists the user's workspaces (cookie-authenticated) and lets them enter one
// or create a new one.
const COLORS = ['#5039BB', '#1D9E75', '#D85A30', '#D4537E', '#185FA5', '#BA7517'];
const initials = (name) =>
  (name || '?')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

// Where the new (v2) UI is mounted on this same host.
const V2_BASE = '/v2';

// Enter a workspace. Workspaces flagged isNewUi go to the new UI (mounted at
// /v2); everyone else stays on the legacy UI. Full navigation so the target app
// bootstraps with the workspace in the URL.
const enterWorkspace = (w) => {
  const wId = w._id;
  localStorage.setItem('userCurrentWorkspaceId', wId);
  if (w.isNewUi) {
    window.location.href = `${V2_BASE}/${wId}`;
  } else {
    window.location.href = `/${wId}/dashboard`;
  }
};

// A brand-new workspace has no Slack/team yet -> onboarding. New workspaces use
// the new UI, so send them to the v2 onboarding wizard.
const startOnboarding = (wId) => {
  localStorage.setItem('userCurrentWorkspaceId', wId);
  window.location.href = `${V2_BASE}/onboarding`;
};

const TrooprWorkspaces = (props) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    axios
      .get('/api/workspaces', { withCredentials: true })
      .then((res) => {
        if (res.data && res.data.success) setWorkspaces(res.data.workspaces || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const createWorkspace = () => {
    setError('');
    const name = newName.trim();
    if (!name) return setError('Enter a workspace name');
    setCreating(true);
    axios
      .post('/api/workspace.create', { name }, { withCredentials: true })
      .then((res) => {
        setCreating(false);
        if (res.data && res.data.success && res.data.workspace) {
          startOnboarding(res.data.workspace._id);
        } else {
          setError((res.data && res.data.message) || 'Could not create workspace');
        }
      })
      .catch(() => {
        setCreating(false);
        setError('Could not create workspace. Try again.');
      });
  };

  const signOut = () => {
    Promise.resolve(props.logout()).finally(() => {
      window.location.href = '/troopr/access';
    });
  };

  const openCreate = () => {
    setError('');
    setShowCreate(true);
  };
  const closeCreate = () => {
    setError('');
    setNewName('');
    setShowCreate(false);
  };

  if (loading) {
    return (
      <AuthShell>
        <h1 className="tpr-auth__title">Welcome back!</h1>
        <p className="tpr-auth__subtitle">Choose your workspace</p>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin />
        </div>
      </AuthShell>
    );
  }

  // Dedicated create screen — focused, no workspace list behind it.
  if (showCreate) {
    return (
      <AuthShell>
        <h1 className="tpr-auth__title">Create a workspace</h1>
        <p className="tpr-auth__subtitle">
          Give it a name to get started. You'll connect Slack next.
        </p>

        {error && <span className="tpr-auth__error">* {error}</span>}

        <div className="tpr-auth__field" style={{ marginTop: 16 }}>
          <Input
            autoFocus
            className="tpr-auth__input"
            placeholder="e.g. Acme Engineering"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={createWorkspace}
            style={{ marginBottom: 12 }}
          />
          <Button
            className="tpr-auth__primary"
            type="primary"
            block
            loading={creating}
            onClick={createWorkspace}
          >
            Create workspace
          </Button>
        </div>

        <div className="tpr-auth__footer">
          <span className="tpr-auth__link" onClick={closeCreate}>
            ‹ Back to your workspaces
          </span>
        </div>
      </AuthShell>
    );
  }

  const isEmpty = workspaces.length === 0;

  return (
    <AuthShell>
      <h1 className="tpr-auth__title">
        {isEmpty ? 'Welcome to Troopr' : 'Welcome back!'}
      </h1>
      <p className="tpr-auth__subtitle">
        {isEmpty ? "Let's set up your first workspace" : 'Choose your workspace'}
      </p>

      {isEmpty ? (
        <div className="tpr-ws__empty">
          <span className="tpr-ws__empty-icon">
            <svg
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="7" height="7" rx="1.5" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" />
              <path d="M17.5 14v7M14 17.5h7" />
            </svg>
          </span>
          <div className="tpr-ws__empty-title">Create your first workspace</div>
          <div className="tpr-ws__empty-sub">
            A workspace is where your team, Slack and tools live. You'll connect
            Slack right after.
          </div>
          <Button
            className="tpr-auth__primary"
            type="primary"
            block
            onClick={openCreate}
            style={{ marginTop: 20 }}
          >
            Create workspace
          </Button>
        </div>
      ) : (
        <>
          <div className="tpr-ws__list">
            {workspaces.map((w, i) => (
              <div
                key={w._id}
                className="tpr-ws__row"
                onClick={() => enterWorkspace(w)}
              >
                <span
                  className="tpr-ws__avatar"
                  style={{ background: COLORS[i % COLORS.length] }}
                >
                  {initials(w.name)}
                </span>
                <span className="tpr-ws__name">
                  {w.name || 'Untitled workspace'}
                </span>
                <span className="tpr-ws__chevron">›</span>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button className="tpr-ws__create" onClick={openCreate}>
              Create new workspace
            </Button>
          </div>
        </>
      )}

      <div className="tpr-auth__footer">
        Not seeing your workspace?{' '}
        <span className="tpr-auth__link" onClick={signOut}>
          Try a different email address
        </span>
      </div>
    </AuthShell>
  );
};

export default withRouter(connect(null, { logout })(TrooprWorkspaces));
